import { useEffect, useState } from 'react';
import { Card, CardTitle, CardDescription, buttonClasses, useAuth, useBrand, brandDisplayName, titleCaseName } from '@aralere/shared-ui';
import type { MeResponse } from '@aralere/shared-types';

export function ClientDashboard(): JSX.Element {
  const { user } = useAuth();
  const { brand } = useBrand();
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    fetch('/api/me')
      .then((r) => (r.ok ? r.json() : null))
      .then(setMe)
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display text-3xl tracking-tight">
          {greeting()}, {user?.displayName ? titleCaseName(user.displayName).split(' ')[0] : 'there'}
        </h1>
        <p className="text-sm text-[var(--color-muted)]">
          {brand ? brandDisplayName(brand.name) : ''} client portal
        </p>
      </header>

      <section>
        <h2 className="font-display text-lg mb-3">Your services</h2>
        {me?.enabledServices && me.enabledServices.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {me.enabledServices.map(({ service, clientService }) => (
              <Card key={clientService.id} className="transition hover:shadow-md">
                <div className="flex items-center gap-3 mb-2">
                  {service.logoUrl ? (
                    <img src={service.logoUrl} alt="" className="h-8 w-8" />
                  ) : (
                    <div className="h-8 w-8 rounded bg-[var(--color-accent)]/30" />
                  )}
                  <CardTitle>{service.shortName}</CardTitle>
                </div>
                <CardDescription>{service.description}</CardDescription>
                <div className="mt-3 text-xs">
                  {service.ssoType === 'admin-only' ? (
                    <span className="inline-flex items-center gap-1 text-[var(--tint-green-text)]">
                      <span className="h-2 w-2 rounded-full bg-green-600" /> Active
                    </span>
                  ) : service.launchUrl ? (
                    <a
                      href={service.launchUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={buttonClasses('primary', 'sm')}
                    >
                      Open ↗
                    </a>
                  ) : null}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <p className="text-sm text-[var(--color-muted)]">
              No active services yet. Your administrator will enable services here.
            </p>
          </Card>
        )}
      </section>

      <section>
        <h2 className="font-display text-lg mb-3">Account</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="opacity-60">
            <CardTitle>Open invoices</CardTitle>
            <CardDescription>None at the moment.</CardDescription>
          </Card>
          <Card className="opacity-60">
            <CardTitle>Recent payments</CardTitle>
            <CardDescription>No payments to show.</CardDescription>
          </Card>
        </div>
      </section>
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
